import {Injectable} from '@angular/core';
import {
  isKIOSKPOLIS,
  isMPay,
  isOLX,
  isSkycash,
  salesChannel,
  isNext,
  isCarOne,
  isNestBank
} from '../../core/utils/utils';
import {SalesChannel} from '../../core/consts/sales-channel.enum';
import {UrlParamsService} from '../../core/services/impl/url-params.service';
import {QueryParamName} from '../../core/enums/query-param-name.enum';

@Injectable({
  providedIn: 'root'
})
export class AssetsService {

  salesChannelsDirectories: SalesChannelDirectory[] = [
    {
      salesChannel: SalesChannel.YANOSIK,
      dirName: 'yanosik'
    },
    {
      salesChannel: SalesChannel.BANKIER,
      dirName: 'bankier'
    },
    {
      salesChannel: SalesChannel.INTERCARS,
      dirName: 'intercars'
    },
    {
      salesChannel: SalesChannel.OLX,
      dirName: 'olx'
    },
    {
      salesChannel: SalesChannel.MPAY,
      dirName: 'mpay'
    },
    {
      salesChannel: SalesChannel.SKYCASH,
      dirName: 'skycash'
    },
    {
      salesChannel: SalesChannel.CARONE,
      dirName: 'car-one'
    },
    {
      salesChannel: SalesChannel.NESTBANK,
      dirName: 'nest-bank'
    }
  ];
  isNext = isNext();

  readonly VEHICLE_CHOOSER_ICONS_DIR = 'assets/images/vehicle-chooser';
  readonly BANNERS_ICONS_DIR = 'assets/images/banners';
  readonly ICONS_MAIN_DIR = 'assets/images/icons';

  get informationDialogImageSrc(): string {
    if (isMPay()) {
      return 'assets/images/information-dialog-mpay.png';
    }
    if (isSkycash()) {
      return 'assets/images/information-dialog-skycash.png';
    }
    if (isNestBank()) {
      return 'assets/images/nest-bank-keys.png';
    }
    if (this.isNext) {
      return 'assets/images/information-dialog-next.png';
    }
    return 'assets/images/information-dialog.png';
  }

  hasAdditionalLogo(partnerName: string): boolean {
    const partner = this.urlParamsService.getQueryParam(QueryParamName.PARTNER);
    return partner === partnerName;
  }

  constructor(private urlParamsService: UrlParamsService) {
  }

  getIconsDirectoryPath(ignoreSalesChannel: boolean = false): string {
    const subdirectory = this.getSalesChannelDirName() && !ignoreSalesChannel ? `/${this.getSalesChannelDirName()}` : '';
    return `${this.ICONS_MAIN_DIR}${subdirectory}`;
  }

  getVehicleChooserIconsDirectoryPath(): string {
    const subdirectory = this.getSalesChannelDirName() ? `/${this.getSalesChannelDirName()}` : '';
    return `${this.VEHICLE_CHOOSER_ICONS_DIR}${subdirectory}`;
  }

  getBannersIconsDirectoryPath(): string {
    const subdirectory = this.getSalesChannelDirName() ? `/${this.getSalesChannelDirName()}` : '';
    return `${this.BANNERS_ICONS_DIR}${subdirectory}`;
  }

  getAdditionalLogo(): AdditionalLogo {
    if (isOLX()) {
      return {
        src: './assets/images/logo/olx-logo-charcoal-rgb.svg',
        alt: 'olx',
        kpLogoSrc: './assets/images/logo/kioskpolis-dark-text.svg',
        url: 'https://www.olx.pl/'
      }
    }
    if (isMPay()) {
      return {
        src: './assets/images/logo/logo_mPay.png',
        alt: 'mPay',
        kpLogoSrc: './assets/images/logo/kioskpolis-rgb.svg',
        url: 'http://ubezpieczenia.mpay.pl/'
      }
    }
    if (isSkycash()) {
      return {
        src: './assets/images/logo/logo_skycash.svg',
        alt: 'SkyCash',
        kpLogoSrc: './assets/images/logo/kioskpolis-rgb.svg',
        url: 'http://ubezpieczenia.skycash.pl/'
      }
    }
    if (isKIOSKPOLIS() && this.hasAdditionalLogo('salus')) {
      return {
        src: './assets/images/logo/logo_salus.png',
        alt: 'salus',
        kpLogoSrc: './assets/images/logo/kioskpolis-rgb.svg',
        partnerLogo: true
      }
    }
    if (isCarOne()) {
      return {
        src: './assets/images/logo/car-one-logo.svg',
        alt: 'CarOne',
        kpLogoSrc: './assets/images/logo/kioskpolis-rgb.svg',
      }
    }
    if (isNestBank()) {
      return {
        src: './assets/images/logo/nestbank-logo.svg',
        alt: 'NestBank',
        kpLogoSrc: './assets/images/logo/kioskpolis-rgb.svg',
      }
    }
    return null;
  }

  private getSalesChannelDirName(): string | null {
    const dirConfig = this.salesChannelsDirectories.find(config => config.salesChannel === salesChannel());
    return dirConfig ? dirConfig.dirName : null;
  }
}

export interface AdditionalLogo {
  src: string;
  alt: string;
  kpLogoSrc: string;
  partnerLogo?: boolean;
  url?: string;
}

export interface SalesChannelDirectory {
  salesChannel: SalesChannel;
  dirName: string;
}
